import { format, isValid, parseISO } from "date-fns";

/**
 * Safely parse a date string and return a Date object or null if invalid
 */
export function parseAndValidateDate(dateString: string): Date | null {
  try {
    const date = parseISO(dateString);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
}

/**
 * Parse and format a date string with a fallback for invalid dates
 */
export function formatDateSafely(
  date: string | Date,
  formatString = "MMM d, yyyy h:mm a",
  fallback = "Invalid date",
): string {
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    return isValid(dateObj) ? format(dateObj, formatString) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Parse and format a date string for display with common formats
 */
export function parseDateForDisplay(
  dateString: string,
  formatType: "datetime" | "date" | "time" = "datetime",
): string {
  const formatMap = {
    datetime: "MMM d, yyyy h:mm a",
    date: "MMM d, yyyy",
    time: "h:mm a",
  };

  return formatDateSafely(dateString, formatMap[formatType]);
}

/**
 * Parse and format a time string for display
 */
export function parseTimeForDisplay(dateString: string): string {
  return formatDateSafely(dateString, "h:mm a", "Invalid time");
}

/**
 * Parse and format a short time string for display (e.g., logs)
 */
export function parseShortTimeForDisplay(dateString: string): string {
  return formatDateSafely(dateString, "h:mm:ss a", "Invalid time");
}

/**
 * Calculate duration between two date strings safely
 */
export function calculateDurationSafely(
  startTime: string | Date,
  endTime: string | Date,
  unit: "minutes" | "hours" = "minutes",
): string {
  try {
    const start =
      typeof startTime === "string" ? parseISO(startTime) : startTime;
    const end = typeof endTime === "string" ? parseISO(endTime) : endTime;

    if (!isValid(start) || !isValid(end)) {
      return "Invalid duration";
    }

    const diffMs = end.getTime() - start.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));

    if (unit === "hours") {
      const hours = Math.round(diffMinutes / 60);
      return `${hours} hour${hours === 1 ? "" : "s"}`;
    }

    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"}`;
  } catch {
    return "Invalid duration";
  }
}
